/**
 * 경로 매칭 및 타이틀 해석. route-patterns만 사용하여 순환 의존성 없음.
 * useGobackTab, useForwardTab 등에서 getTabTitle 사용.
 */
import { getDefaultStore } from 'jotai';
import type { TabContext } from '@/entities/tab/model/tab.types';
import { ROUTE_PATTERNS, type RoutePattern } from './route-patterns';
import { filteredRouteConfigsAtom } from '@/entities/workspace-config';

export function matchRoute(
  context: TabContext,
  route: { path: string[] }
): { matched: boolean; params: Record<string, string> } {
  const path = context.path ?? [];
  const pattern = route.path;

  if (path.length !== pattern.length) return { matched: false, params: {} };

  const params: Record<string, string> = {};

  for (let i = 0; i < pattern.length; i++) {
    const patternSegment = pattern[i];
    const pathSegment = path[i];

    if (patternSegment.startsWith(':')) {
      const paramName = patternSegment.slice(1);
      if (!pathSegment || pathSegment.length === 0) {
        return { matched: false, params: {} };
      }
      params[paramName] = pathSegment;
      continue;
    }

    if (patternSegment !== pathSegment) {
      return { matched: false, params: {} };
    }
  }

  return { matched: true, params };
}

function findRoutePattern(context: TabContext): RoutePattern {
  for (const route of ROUTE_PATTERNS) {
    if (route.path[0] === '404') continue;
    const match = matchRoute(context, route);
    if (match.matched) return route;
  }
  const notFoundRoute = ROUTE_PATTERNS.find((r) => r.path[0] === '404');
  return notFoundRoute ?? ROUTE_PATTERNS[ROUTE_PATTERNS.length - 1];
}

function resolveTitle(
  title: string,
  pathParams: Record<string, string>,
  queryParams: Record<string, unknown>
): string {
  const DOUBLE_ESCAPED_QUESTION_PLACEHOLDER = '\u0001';
  const DOUBLE_ESCAPED_COLON_PLACEHOLDER = '\u0002';
  const ESCAPED_QUESTION_PLACEHOLDER = '\u0003';
  const ESCAPED_COLON_PLACEHOLDER = '\u0004';

  return title
    .replace(/\\\\\?/g, DOUBLE_ESCAPED_QUESTION_PLACEHOLDER)
    .replace(/\\\\:/g, DOUBLE_ESCAPED_COLON_PLACEHOLDER)
    .replace(/\\\?/g, ESCAPED_QUESTION_PLACEHOLDER)
    .replace(/\\:/g, ESCAPED_COLON_PLACEHOLDER)
    .replace(/:(\w+)/g, (match, varName) => pathParams[varName] ?? match)
    .replace(/\?(\w+)/g, (match, varName) => {
      const value = queryParams[varName];
      return value != null ? String(value) : match;
    })
    .replace(ESCAPED_QUESTION_PLACEHOLDER, '?')
    .replace(ESCAPED_COLON_PLACEHOLDER, ':')
    .replace(DOUBLE_ESCAPED_QUESTION_PLACEHOLDER, '\\?')
    .replace(DOUBLE_ESCAPED_COLON_PLACEHOLDER, '\\:');
}

export function getTabTitle(context: TabContext): string {
  if (!context.path?.length) return '404';

  try {
    const routeConfigs = getDefaultStore().get(filteredRouteConfigsAtom);
    for (const route of routeConfigs) {
      if (route.path[0] === '404') continue;
      const match = matchRoute(context, { path: [...route.path] });
      if (match.matched) {
        return resolveTitle(route.title, match.params, context.params);
      }
    }
    const notFoundRoute = routeConfigs.find((r) => r.path[0] === '404');
    if (notFoundRoute) return notFoundRoute.title;
  } catch {
    // atom/store not ready, fall through to ROUTE_PATTERNS
  }

  const matchedRoute = findRoutePattern(context);
  const match = matchRoute(context, matchedRoute);
  if (!match.matched) return matchedRoute.title;
  return resolveTitle(matchedRoute.title, match.params, context.params);
}
